// Light-weight device fingerprint helper.
//
// Collects a small set of high-entropy signals that the user has already
// shared with every site they visit (UA, screen, timezone, language) plus
// a stable per-browser UUID stored in localStorage AND a cookie so that
// clearing one of them doesn't reset the fingerprint.
//
// We hash the canvas drawing client-side to keep the payload small; the
// backend re-hashes everything with the IP /24 prefix for the actual key.

const STORAGE_KEY = "mediquizit_device_uuid_v1";
const COOKIE_KEY = "mqit_dev";
const COOKIE_DAYS = 365;

function getOrCreateUuid() {
  try {
    const fromLs = window.localStorage.getItem(STORAGE_KEY);
    if (fromLs) return fromLs;
  } catch { /* private mode */ }
  try {
    const fromCookie = (document.cookie || "")
      .split(";").map((c) => c.trim())
      .find((c) => c.startsWith(`${COOKIE_KEY}=`));
    if (fromCookie) {
      const v = decodeURIComponent(fromCookie.split("=")[1] || "");
      if (v) {
        try { window.localStorage.setItem(STORAGE_KEY, v); } catch {}
        return v;
      }
    }
  } catch {}
  // Generate fresh UUID.
  const uuid = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
  try { window.localStorage.setItem(STORAGE_KEY, uuid); } catch {}
  try {
    const expires = new Date(Date.now() + COOKIE_DAYS * 86400_000).toUTCString();
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(uuid)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {}
  return uuid;
}

function canvasHash() {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 220; canvas.height = 30;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Medicos·para·Italia", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Medicos·para·Italia", 4, 17);
    const data = canvas.toDataURL();
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      hash = ((hash << 5) - hash + data.charCodeAt(i)) | 0;
    }
    return `c${hash >>> 0}`;
  } catch {
    return "";
  }
}

export function buildFingerprintComponents() {
  const screen = (typeof window !== "undefined" && window.screen) ? window.screen : {};
  const tz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; }
    catch { return ""; }
  })();
  return {
    user_agent: (navigator && navigator.userAgent) || "",
    screen: `${screen.width || 0}x${screen.height || 0}`,
    color_depth: screen.colorDepth || 0,
    hw_concurrency: (navigator && navigator.hardwareConcurrency) || 0,
    timezone: tz,
    language: (navigator && (navigator.language || navigator.userLanguage)) || "",
    canvas_hash: canvasHash(),
    client_uuid: getOrCreateUuid(),
  };
}

let _registered = false;
let _cached = null;

export async function registerDevice(apiPost) {
  if (_registered && _cached) return _cached;
  try {
    const components = buildFingerprintComponents();
    const result = await apiPost("/device/register", { components });
    _registered = true;
    _cached = result;
    return result;
  } catch {
    return null;
  }
}

export function getCachedDeviceRegistration() {
  return _cached;
}

export function clearDeviceCache() {
  _registered = false;
  _cached = null;
}
